/**
 * Автоопределение сущностей — ТОЛЬКО для превью. §Хребет данных 3.5, 05-ui.md §3.3.
 *
 * Разделение обязанностей, нормативное:
 *   canonical = canonicalize(draft)             // подписывается, хэшируется, отправляется
 *   detected  = detectEntities(canonical.text)  // display-only, НИКОГДА не отправляется
 *   expected  = expectedServerEntities(canonical)
 *
 * `expected` существует ТОЛЬКО для превью. Сверка после публикации (compareSentMessage)
 * строит ожидаемую сторону прямо из canonical.entities и сюда не заглядывает: связывать
 * вердикт сверки с версией НАШЕГО детектора ссылок нельзя — обновление TDLib меняло бы
 * вердикт, не меняя ни одного нашего байта.
 *
 * canonicalize() об этом модуле не знает вовсе: автоопределённые сущности в канонический
 * контент не попадают никогда, иначе наша каноническая форма зависела бы от версии TDLib
 * на серверах Telegram.
 */

import type { MessageEntity } from '../draft.ts';
import { sortEntities } from '../entity.ts';
import { resplitEntities } from '../resplit.ts';
import type { PreviewEntity } from '../tree.ts';
import { findMentions, matchHashtags } from './match.ts';
import { findUrls } from './url.ts';

export { category } from './match.ts';
export { findUrls, fixUrl, isCommonTld, isEmailAddress } from './url.ts';

export type DetectedType = 'mention' | 'hashtag' | 'url' | 'email';

export interface DetectedEntity {
  type: DetectedType;
  offset: number;
  length: number;
}

const overlaps = (
  a: { offset: number; length: number },
  b: { offset: number; length: number },
): boolean => a.offset < b.offset + b.length && b.offset < a.offset + a.length;

const contains = (
  outer: { offset: number; length: number },
  inner: { offset: number; length: number },
): boolean =>
  outer.offset <= inner.offset && outer.offset + outer.length >= inner.offset + inner.length;

/**
 * Порядок добавления — как в TDLib find_entities: mention → hashtag → url/email.
 * Результат отсортирован по offset и не пересекается сам с собой.
 */
export function detectEntities(text: string): DetectedEntity[] {
  const out: DetectedEntity[] = [];
  const claim = (e: DetectedEntity): void => {
    for (const already of out) {
      if (overlaps(already, e)) return;
    }
    out.push(e);
  };

  for (const m of findMentions(text))
    claim({ type: 'mention', offset: m.offset, length: m.length });
  for (const m of matchHashtags(text))
    claim({ type: 'hashtag', offset: m.offset, length: m.length });
  for (const m of findUrls(text)) claim({ type: m.type, offset: m.offset, length: m.length });

  out.sort((a, b) => a.offset - b.offset);
  return out;
}

const QUOTE_CLASS: ReadonlySet<string> = new Set(['blockquote', 'expandable_blockquote']);
const CONTINUOUS_CLASS: ReadonlySet<string> = new Set(['code', 'pre', 'text_link']);

/**
 * Порт merge_new_entities: автоопределённая сущность выбрасывается, если пересекает границу
 * цитаты или пересекается с continuous-сущностью (text_link, code, pre) — сервер её там
 * и не поставит. Splittable сосуществуют и при необходимости разрезаются resplitEntities.
 */
export function mergeNewEntities(
  explicit: readonly MessageEntity[],
  detected: readonly DetectedEntity[],
): PreviewEntity[] {
  const quotes = explicit.filter((e) => QUOTE_CLASS.has(e.type));
  const continuous = explicit.filter((e) => CONTINUOUS_CLASS.has(e.type));

  const kept = detected.filter((d) => {
    for (const c of continuous) {
      if (overlaps(c, d)) return false;
    }
    for (const q of quotes) {
      if (overlaps(q, d) && !contains(q, d)) return false;
    }
    return true;
  });

  // Автоопределённые не участвуют в resplit как splittable: они всегда целые интервалы,
  // но явные splittable должны разрезаться по их границам — ровно это делает сервер.
  const { entities } = resplitEntities([...explicit, ...(kept as unknown as MessageEntity[])]);
  return sortEntities(entities as unknown as PreviewEntity[]);
}

/** То, что покажет клиент подписчика: наша разметка плюс подсветка, которую добавит сервер. */
export function expectedServerEntities(canonical: {
  text: string;
  entities: readonly MessageEntity[];
}): PreviewEntity[] {
  return mergeNewEntities(canonical.entities, detectEntities(canonical.text));
}
